import { supabase } from "../lib/supabase";

export interface AddressData {
  street: string;
  house_number: string;
  zip_code: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  created_by_user_id: string;
}

export interface RegenerationAddressInput {
  street: string;
  house_number: string;
  zip_code: string;
  city: string;
  country: string;
  created_by_user_id?: string | null;
}

const normalizeAddressField = (value: string): string => value.trim();

interface AddressLookupOptions {
  zipCode?: string;
  city?: string;
  country?: string;
}

/**
 * Find an existing address by street and house number, with optional filters.
 * Used for UI display ("existing address found" badge), NOT for save-path dedup.
 */
export async function findAddressByStreetAndHouseNumber(
  street: string,
  houseNumber: string,
  options?: AddressLookupOptions
): Promise<(AddressData & { id: number }) | null> {
  if (!street || !houseNumber) return null;

  let query = supabase
    .from("addresses")
    .select("*")
    .eq("street", street)
    .eq("house_number", houseNumber);

  if (options?.zipCode) {
    query = query.eq("zip_code", options.zipCode);
  }

  if (options?.city) {
    query = query.eq("city", options.city);
  }

  if (options?.country) {
    query = query.eq("country", options.country);
  }

  const { data, error } = await query.limit(1);

  if (error) {
    console.error("Error finding address:", error);
    throw error;
  }

  return data && data.length > 0 ? data[0] : null;
}

/**
 * Save an address atomically using the DB upsert_address function.
 * If the address already exists (matched by street + house_number + zip_code),
 * the existing row is returned instead of creating a duplicate.
 */
export async function saveAddress(
  address: AddressData
): Promise<AddressData & { id: number }> {
  const { data, error } = await supabase.rpc("upsert_address", {
    p_street: address.street.trim(),
    p_house_number: address.house_number.trim(),
    p_zip_code: address.zip_code.trim(),
    p_city: address.city.trim(),
    p_country: address.country.trim(),
    p_latitude: address.latitude,
    p_longitude: address.longitude,
    p_created_by_user_id: address.created_by_user_id,
  });

  if (error) {
    console.error("Error upserting address:", error);
    throw error;
  }

  if (!data) {
    throw new Error("Failed to upsert address");
  }

  return data as AddressData & { id: number };
}

/**
 * Resolve or create an address for PDF regeneration using the DB function.
 */
export async function resolveOrCreateAddressForRegeneration(
  address: RegenerationAddressInput
): Promise<{ id: number }> {
  const street = normalizeAddressField(address.street);
  const houseNumber = normalizeAddressField(address.house_number);
  const zipCode = normalizeAddressField(address.zip_code);
  const city = normalizeAddressField(address.city);
  const country = normalizeAddressField(address.country);

  if (!street || !houseNumber || !zipCode || !city) {
    throw new Error(
      "Street, house number, zip code and city are required for address resolution"
    );
  }

  const { data, error } = await supabase.rpc(
    "resolve_or_create_address_for_regeneration",
    {
      p_street: street,
      p_house_number: houseNumber,
      p_zip_code: zipCode,
      p_city: city,
      p_country: country,
      p_created_by_user_id: address.created_by_user_id ?? null,
    }
  );

  if (error) {
    console.error("Error resolving regeneration address:", error);
    throw error;
  }

  const resolvedId = Number(data);
  if (!Number.isFinite(resolvedId) || resolvedId <= 0) {
    throw new Error("Failed to resolve address ID for regeneration");
  }

  return { id: resolvedId };
}
